import React from 'react';
import { useTheme, Fade, Grid, Card, CardContent, Box, Typography } from '@mui/material';
import {
  Handshake as HandshakeIcon,
  GroupWork as GroupWorkIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
  PersonOff as PersonOffIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';

const PairingTab = ({ data = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const totalPairs = data.totalPairs || 0;
  const pairedTeachers = data.pairedTeachers || 0;
  const unpairedTeachers = data.unpairedTeachers || 0;
  const pairedEdifiers = data.pairedEdifiers || 0;
  const unpairedEdifiers = data.unpairedEdifiers || 0;
  const pairingRate = data.pairingRate || 0;

  // Calculate totals for percentages
  const totalTeachers = pairedTeachers + unpairedTeachers;
  const totalEdifiers = pairedEdifiers + unpairedEdifiers;
  const totalAll = totalTeachers + totalEdifiers;

  // Treemap chart for pairing visualization
  const treemapData = {
    series: [
      {
        data: [
          { 
            x: 'Paired Teachers', 
            y: pairedTeachers,
            fillColor: '#2e7d32'
          },
          { 
            x: 'Unpaired Teachers', 
            y: unpairedTeachers,
            fillColor: '#d32f2f'
          },
          { 
            x: 'Paired Edifiers', 
            y: pairedEdifiers,
            fillColor: '#1976d2'
          },
          { 
            x: 'Unpaired Edifiers', 
            y: unpairedEdifiers,
            fillColor: '#ed6c02'
          },
        ],
      },
    ],
    options: {
      chart: {
        type: 'treemap',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      dataLabels: {
        enabled: true,
        style: { 
          fontSize: '14px', 
          fontWeight: 700,
          colors: ['#FFFFFF']
        },
        formatter: (text, op) => {
          const value = op.value || 0;
          const percentage = totalAll > 0 ? ((value / totalAll) * 100).toFixed(1) : 0;
          return [`${text}`, `${value} (${percentage}%)`];
        },
        offsetY: -4,
      },
      plotOptions: {
        treemap: {
          enableShades: true,
          shadeIntensity: 0.3,
          reverseNegativeShade: false,
          borderRadius: 4,
          useFillColorAsStroke: false,
          distributed: true,
        },
      },
      colors: ['#2e7d32', '#d32f2f', '#1976d2', '#ed6c02'],
      tooltip: {
        enabled: true,
        style: {
          fontSize: '14px',
        },
        y: {
          formatter: (value, { seriesIndex, dataPointIndex }) => {
            const labels = ['Paired Teachers', 'Unpaired Teachers', 'Paired Edifiers', 'Unpaired Edifiers'];
            const label = labels[dataPointIndex] || '';
            const percentage = totalAll > 0 ? ((value / totalAll) * 100).toFixed(1) : 0;
            return `<div style="padding: 8px;">
              <strong>${label}</strong><br/>
              <span>Count: ${value}</span><br/>
              <span>Percentage: ${percentage}%</span>
            </div>`;
          },
        },
        marker: {
          show: true,
        },
      },
      legend: {
        show: false,
      },
    },
  };

  // Bubble chart for pairing comparison
  const bubbleData = {
    series: [
      {
        name: 'Teachers',
        data: [
          { x: pairedTeachers, y: unpairedTeachers, z: pairedTeachers + unpairedTeachers },
        ],
      },
      {
        name: 'Edifiers',
        data: [
          { x: pairedEdifiers, y: unpairedEdifiers, z: pairedEdifiers + unpairedEdifiers },
        ],
      },
    ],
    options: {
      chart: {
        type: 'bubble',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
      },
      xaxis: {
        title: { text: 'Paired', style: { fontSize: '14px', fontWeight: 600 } },
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      yaxis: {
        title: { text: 'Unpaired', style: { fontSize: '14px', fontWeight: 600 } },
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      colors: ['#2e7d32', '#1976d2'],
      dataLabels: {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 'bold' },
      },
      legend: {
        position: 'top',
        fontSize: '14px',
        fontWeight: 500,
      },
      fill: {
        opacity: 0.8,
      },
    },
  };

  return (
    <Fade in timeout={500}>
      <Grid container spacing={3}>
        {/* Center Large Card */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0px 4px 12px 0px rgba(102, 126, 234, 0.3)',
              borderRadius: 2,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <HandshakeIcon sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
              {totalPairs}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, mb: 2 }}>
              Total Active Pairs
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                  {pairingRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Pairing Rate
                </Typography>
              </Box>
              <Box sx={{ width: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                  {pairedTeachers + pairedEdifiers}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Total Paired
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Pairing Stats - Horizontal Layout */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              borderLeft: '4px solid #2e7d32',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon sx={{ fontSize: 32, color: 'success.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  {pairedTeachers}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Paired Teachers
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              borderLeft: '4px solid #d32f2f',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonOffIcon sx={{ fontSize: 32, color: 'error.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  {unpairedTeachers}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Unpaired Teachers
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              borderLeft: '4px solid #1976d2',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SupervisorAccountIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  {pairedEdifiers}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Paired Edifiers
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
              borderLeft: '4px solid #ed6c02',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonOffIcon sx={{ fontSize: 32, color: 'warning.main' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  {unpairedEdifiers}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Unpaired Edifiers
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Navigate to Pairing Management */}
        <Grid item xs={12}>
          <Card
            onClick={() => navigate('/dashboard/manage-pairings')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
              boxShadow: '0 4px 16px rgba(168,70,155,0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(168,70,155,0.45)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
                  Manage Pairings
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  View and manage all teacher-edifier pairs
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Treemap Chart - Full Width */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupWorkIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                  Pairing Distribution
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#2e7d32', borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    Paired
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#d32f2f', borderRadius: 1 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                    Unpaired
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Chart options={treemapData.options} series={treemapData.series} type="treemap" height={400} />
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                Size represents the number of users. Hover over each section for detailed information.
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default PairingTab;

